import { isNonEmpty } from '@sgme/fp';
import { ColTypeDef, ICellEditorParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { LabelItem } from 'components/shared/compact-selector/types';
import { useMemo } from 'react';
import { LabelCellRenderer } from 'routes/secondary-market-batch/grid/components/label-cell-renderer';
import { formatCustodianLabel } from 'routes/secondary-market-batch/grid/hooks/use-custodian-column-type';
import { equalsById } from 'routes/secondary-market-batch/grid/utils/equals-by-id';
import { RowData } from 'state/workspaces/secondary-market/batch/model';

const EMPTY_OPTION = null;

const defaultFormatLabelItem = (value: LabelItem | undefined | null): string => value?.label ?? '';

export function useLabeledOptionListColumnType(orderType: OrderType): ColTypeDef<RowData, RowData[keyof RowData]> {
  const formatter = orderType === 'ebiz-quote' ? formatCustodianLabel : defaultFormatLabelItem;

  return useMemo(
    () => ({
      cellEditor: 'agRichSelectCellEditor',
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }: ValueFormatterParams<RowData>) => {
          return formatter(value as LabelItem | undefined) || null;
        },
      },
      filterValueGetter: ({ getValue, colDef }: ValueGetterParams<RowData>) => {
        return formatter((colDef.field ? getValue(colDef.field) : undefined) as LabelItem | undefined);
      },
      valueFormatter: ({ value }: ValueFormatterParams<RowData>) => {
        return formatter(value as LabelItem | undefined);
      },
      equals: equalsById,
      keyCreator: ({ value }) => (value as LabelItem | undefined)?.id ?? '',
      cellRenderer: LabelCellRenderer,
      cellEditorParams: {
        multiSelect: false,
        allowTyping: true,
        filterList: true,
        highlightMatch: true,
        values: ({ colDef, data }: ICellEditorParams<RowData, LabelItem | null>) => {
          const options = data.metadata[colDef.context.metadataId] as LabelItem[] | undefined;
          const validOptions = options?.filter(option => isNonEmpty(option.id) && isNonEmpty(option.label)) ?? [];
          return validOptions.length > 0 ? [EMPTY_OPTION, ...validOptions] : validOptions;
        },
        formatValue: formatter,
      },
    }),
    [formatter],
  );
}
